/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { WizardStepBeforeNextEvent } from '@gh/common-internal-shared';
import {
  createDialogRoute,
  formStateSnapshot,
  FormStateSnapshot,
  getDialogInitData,
  getFormStateSnapshotValue,
  markFormStateAsTouched,
  IntercomService,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogInput, NotificationService, WaitingService } from '@gh/core-ui';
import { first, isNil } from 'lodash';
import {
  CompleteSaleDialogConfig,
  LeadDetailsBusinessService,
} from '../../resources/lead-details-business.service';
import {
  isQuoteCanBeCompleted,
  SfCompletionAuthorization,
  sfCompletionAuthorizationFromQuote,
  SfCompletionCheckListItem,
  SfCompletionQuoteListItem,
  SfCompletionRequest,
  validateCompletionAuthorization,
} from '../../resources/lead.model';

@Component({
  standalone: false,
  selector: 'gh-complete-sale-dialog',
  templateUrl: 'complete-sale-dialog.component.html',
  styleUrls: ['complete-sale-dialog.component.scss'],
})
export class CompleteSaleDialogComponent implements OnInit, AfterViewInit, DialogInput<CompleteSaleDialogConfig> {
  input: CompleteSaleDialogConfig;

  wizardStepHeaderReady = false;

  quotes: SfCompletionQuoteListItem[];
  checks: SfCompletionCheckListItem[];

  selectedQuote?: SfCompletionQuoteListItem;
  selectedChecks: ID[] = [];
  authorization: FormStateSnapshot<SfCompletionAuthorization>;

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private dialogRef: MatDialogRef<any>,
              private notifications: NotificationService,
              private business: LeadDetailsBusinessService,
              private intercomService: IntercomService,
              private waitings: WaitingService,
              private cdr: ChangeDetectorRef) {

  }

  get customerName(): string {
    return this.input.customerName;
  }

  get salesFolderName(): string {
    return this.input.salesFolderName;
  }

  get dppJobNumber(): Maybe<number> {
    return this.input.dppJobNumber;
  }

  get isSelectedQuoteCanBeCompleted(): boolean {
    const { selectedQuote } = this;
    return selectedQuote ? isQuoteCanBeCompleted(selectedQuote) : false;
  }

  get isChecklistCompleted(): boolean {
    return this.checks.length === this.selectedChecks.length;
  }

  ngOnInit(): void {
    const { quotes, checklist } = getDialogInitData(this.dialogData);

    this.quotes = quotes;
    this.checks = checklist;

    const quote = first(quotes);
    if (quote) {
      this.selectQuote(<SfCompletionQuoteListItem>quote);
    }
  }

  ngAfterViewInit(): void {
    this.wizardStepHeaderReady = true;
    this.cdr.detectChanges();
  }

  onValidateSelectedQuote(event: WizardStepBeforeNextEvent): void {
    const { selectedQuote } = this;

    if (isNil(selectedQuote)) {
      event.preventDefault();
      return;
    }

    if (selectedQuote.isDeleted) {
      this.notifications.error('msg_hp_of_quote_deleted');
      event.preventDefault();
    }

    if (!selectedQuote.inspectionCompleted) {
      this.notifications.error('msg_site_of_quote_not_completed');
      event.preventDefault();
    }
  }

  onQuoteSelected(quote: SfCompletionQuoteListItem | undefined): void {
    if (quote == null) {
      return;
    }
    this.selectQuote(quote);
  }

  onChecksChanged(checks: ID[]): void {
    this.selectedChecks = checks;
  }

  onAuthorizationChange(snapshot: FormStateSnapshot<SfCompletionAuthorization>): void {
    this.authorization = snapshot;
  }

  onProcess(): void {
    const { selectedQuote } = this;
    const authorization = getFormStateSnapshotValue(this.authorization);

    if (selectedQuote) {
      if (validateCompletionAuthorization(authorization)) {
        this.authorization = markFormStateAsTouched(this.authorization);
        return;
      }

      const complete: SfCompletionRequest = {
        quotationId: selectedQuote.id,
        description: authorization.description,
        baseDesignId: authorization.baseDesign && authorization.baseDesign.id,
        unitsCount: authorization.unitsCount,
        memo: authorization.memo,
      };

      this.business.completeQuote(this.input.salesFolderId, complete).pipe(
        this.waitings.operator())
        .subscribe(
          () => {
            this.intercomService.trackIntercomEvent('completed-sale');
            this.notifications.success('msg_created_successfully');
            this.dialogRef.close(complete);
          },
          this.notifications.processAll);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private selectQuote(quote: SfCompletionQuoteListItem): void {
    this.selectedQuote = quote;
    this.selectedChecks = [];
    this.authorization = formStateSnapshot({
      ...sfCompletionAuthorizationFromQuote(quote),
      baseDesign: this.input.dppBaseDesign,
      memo: this.input.dppMemo || '',
    });
  }
}

export const completeSaleDialogRoute = createDialogRoute({
  component: CompleteSaleDialogComponent,
  init: {
    service: LeadDetailsBusinessService,
    selector: 'initCompleteSaleDialogView',
  },
  config: {
    disableClose: true,
    width: '1100px',
    height: '700px',
    maxHeight: '99vh',
  },
});
